import React, { useCallback, useEffect, useState } from 'react';
import { FormattedTime, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { interval, lastValueFrom } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';

import { subscribe } from '@swf/core/api/subscribe';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type { DepositResult } from '../../../../../../types/Cashier';
import { DepositStatus } from '../../../../../../types/status';
import { generateDepositResult } from '../../../../../../utils/cashier';

import CommonLayout from './CommonLayout/CommonLayout';
import MobileLayout from './MobileLayout/MobileLayout';
import { TXTWCL_Your_transaction_cannot_be_com_ELL } from './PayconiqRedirect.messages.json';
import { container } from './PayconiqRedirect.scss';
import TabletLayout from './TabletLayout/TabletLayout';

interface Props {
  qrCodeData: string;
  handleStatusChange: () => void;
}

const PAYCONIQ_TIMEOUT_TIME = 15 * 60,
  PAYCONIQ_COLOR = 'rgb(123,49,74)',
  APP_SCHEMES = ['bepgenapp', 'urlapp'];

const PayconiqRedirect: React.FC<Props> = ({ qrCodeData, handleStatusChange }) => {
  const { data } = useWizardContext();
  const { formatMessage } = useIntl();
  const isPhoneDevice = useSelector(select.device.isPhone);
  const isWideMobileDevice = useSelector(select.device.isMobile);
  const [timer, setTimer] = useState(PAYCONIQ_TIMEOUT_TIME);
  const remainTimeDisplay = (
    <FormattedTime
      value={new Date(0, 0, 0, 0, Math.floor(timer / 60), timer % 60)}
      second="numeric"
      minute="numeric"
    />
  );
  const onFallback = useCallback(() => {
    data.depositResult = generateDepositResult(
      DepositStatus.PAYCONIQ_OPEN_APP_FAILED,
      data.paySystemDetails
    );
    handleStatusChange();
  }, [handleStatusChange, data]);
  const onIgnored = useCallback(() => {
    data.depositResult = generateDepositResult(
      DepositStatus.PAYCONIQ_OPEN_APP_FAILED,
      data.paySystemDetails
    );
    handleStatusChange();
  }, [data, handleStatusChange]);

  useEffect(() => {
    const endTimer = new Date();
    endTimer.setSeconds(endTimer.getSeconds() + PAYCONIQ_TIMEOUT_TIME);
    const depositSubscription = subscribe<{ body: DepositResult }>('/user/queue/cashier');
    const response = lastValueFrom(depositSubscription.pipe(take(1)));
    const outOfTime = interval(1000).pipe(takeWhile(() => Date.now() < endTimer.getTime()));

    const subscribeOutOfTime = outOfTime.subscribe(() =>
      setTimer(Math.ceil(Math.abs(endTimer.getTime() - Date.now()) / 1000))
    );

    Promise.race([response, lastValueFrom(outOfTime)]).then((res) => {
      const resultTransaction = res as { body: DepositResult };
      if (resultTransaction?.body) {
        data.depositResult = resultTransaction.body;
      } else {
        data.depositResult = generateDepositResult(DepositStatus.TIMEOUT, data.paySystemDetails);
      }
      handleStatusChange();
    });

    return () => {
      subscribeOutOfTime.unsubscribe();
    };
  }, [data, handleStatusChange]);

  useEffect(() => {
    const errorMessage = formatMessage(TXTWCL_Your_transaction_cannot_be_com_ELL);
    APP_SCHEMES.forEach((schemaName) =>
      window?.starsweb?.app?.registerFallbackUrlForScheme?.(schemaName, errorMessage)
    );
  }, [formatMessage]);

  if (isPhoneDevice) {
    return (
      <MobileLayout
        qrCodeData={qrCodeData}
        remainTime={remainTimeDisplay}
        onFallback={onFallback}
        onIgnored={onIgnored}
      />
    );
  }

  return (
    <div className={container}>
      {isWideMobileDevice ? (
        <TabletLayout
          payconiqColor={PAYCONIQ_COLOR}
          qrCodeData={qrCodeData}
          remainTime={remainTimeDisplay}
          onFallback={onFallback}
          onIgnored={onIgnored}
        />
      ) : (
        <CommonLayout
          payconiqColor={PAYCONIQ_COLOR}
          qrCodeData={qrCodeData}
          remainTime={remainTimeDisplay}
        />
      )}
    </div>
  );
};

export default PayconiqRedirect;
